/**
 * WatermarkConfigDialog Component
 *
 * Modal dialog for configuring watermark column for a table
 * Contains tabs for: Column Schema, Sample Data, and Recommendation
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTableColumns,
  fetchTableSampleData,
  updateWatermarkColumn,
  updateTableSyncType,
  fetchWatermarkTables
} from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {  AlertCircle, CheckCircle2, Loader2, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { ColumnSchemaTable } from './ColumnSchemaTable';
import { SampleDataViewer } from './SampleDataViewer';
import { WatermarkRecommendationPanel } from './WatermarkRecommendationPanel';

interface WatermarkConfigDialogProps {
  tableName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function WatermarkConfigDialog({
  tableName,
  isOpen,
  onClose,
  onSuccess
}: WatermarkConfigDialogProps) {
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch columns
  const {
    data: columnsData,
    isLoading: isLoadingColumns,
    error: columnsError
  } = useQuery({
    queryKey: ['table-columns', tableName],
    queryFn: () => fetchTableColumns(tableName),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });

  // Fetch sample data
  const {
    data: sampleData,
    isLoading: isLoadingSample,
    error: sampleError
  } = useQuery({
    queryKey: ['table-sample-data', tableName],
    queryFn: () => fetchTableSampleData(tableName, 10),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });

  // Get table info from cached watermark tables query
  const watermarkTables = queryClient.getQueryData<{ tables: any[] }>(['watermark-tables']);
  const tableInfo = watermarkTables?.tables.find(t => t.tableName === tableName);

  // Update watermark column mutation
  const updateMutation = useMutation({
    mutationFn: (columnName: string) => updateWatermarkColumn(tableName, columnName),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: `Watermark column updated for ${tableName}`,
      });
      queryClient.invalidateQueries({ queryKey: ['watermark-tables'] });
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Update sync type mutation
  const updateSyncTypeMutation = useMutation({
    mutationFn: (syncType: 'INCREMENTAL' | 'FULL_REFRESH') =>
      updateTableSyncType(tableName, syncType, 'Changed to FULL_REFRESH sync - no appropriate watermark column available'),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: `Sync type updated to FULL_REFRESH for ${tableName}`,
      });
      queryClient.invalidateQueries({ queryKey: ['watermark-tables'] });
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Handle save watermark column
  const handleSave = () => {
    if (!selectedColumn) {
      toast({
        title: 'No column selected',
        description: 'Please select a watermark column',
        variant: 'destructive'
      });
      return;
    }

    updateMutation.mutate(selectedColumn);
  };

  // Handle mark as full sync
  const handleMarkAsFullSync = () => {
    updateSyncTypeMutation.mutate('FULL_REFRESH');
  };

  // Auto-select recommended column on load
  const recommendedColumn = columnsData?.recommendedColumn;
  if (recommendedColumn && !selectedColumn && columnsData) {
    setSelectedColumn(recommendedColumn);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Table Details
            {tableInfo?.syncTier && (
              <Badge
                variant="outline"
                className={
                  tableInfo.syncTier === 'CDC' ? 'bg-green-50 text-green-700 border-green-200' :
                  tableInfo.syncTier === 'INCREMENTAL' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  tableInfo.syncTier?.startsWith('FULL_') ? 'bg-orange-50 text-orange-700 border-orange-200' :
                  'bg-gray-50 text-gray-700 border-gray-200'
                }
              >
                {tableInfo.syncTier}
              </Badge>
            )}
            {tableInfo?.syncFrequency && (
              <Badge
                variant="outline"
                className={
                  tableInfo.syncFrequency === 'HOURLY'
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-purple-50 text-purple-700 border-purple-200'
                }
              >
                {tableInfo.syncFrequency}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-medium">{tableName}</span>
                {tableInfo && (
                  <span className="text-muted-foreground">
                    {tableInfo.rowCount.toLocaleString()} rows
                  </span>
                )}
              </div>
              {tableInfo?.currentWatermarkColumn && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Current Watermark:</span>
                  <code className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs">
                    {tableInfo.currentWatermarkColumn}
                  </code>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Only show watermark configuration for INCREMENTAL tables */}
        {tableInfo?.syncTier === 'INCREMENTAL' ? (
          <>
            <div className="py-4 w-full overflow-hidden">
              <Tabs defaultValue="columns" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="columns">
                    Column Schema
                    {columnsData && (
                      <Badge variant="secondary" className="ml-2">
                        {columnsData.columns.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="sample">
                    Sample Data
                    {sampleData && (
                      <Badge variant="secondary" className="ml-2">
                        {sampleData.totalRows} rows
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="recommendation">
                    Recommendation
                    {recommendedColumn && (
                      <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800">
                        Available
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="columns" className="mt-4 min-w-0">
                  <div className="overflow-x-auto">
                    {isLoadingColumns && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-muted-foreground">Loading columns...</span>
                      </div>
                    )}

                    {columnsError && (
                      <div className="flex items-center justify-center py-8 text-destructive">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        <span>Error loading columns: {(columnsError as Error).message}</span>
                      </div>
                    )}

                    {columnsData && (
                      <ColumnSchemaTable
                        columns={columnsData.columns}
                        selectedColumn={selectedColumn}
                        onSelectColumn={setSelectedColumn}
                        recommendedColumn={columnsData.recommendedColumn}
                        allPotentialColumns={columnsData.allPotentialColumns}
                      />
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="sample" className="mt-4 min-w-0">
                  <div className="overflow-x-auto">
                    {isLoadingSample && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-muted-foreground">Loading sample data...</span>
                      </div>
                    )}

                    {sampleError && (
                      <div className="flex items-center justify-center py-8 text-destructive">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        <span>Error loading sample data: {(sampleError as Error).message}</span>
                      </div>
                    )}

                    {sampleData && (
                      <SampleDataViewer
                        data={sampleData}
                        highlightColumn={selectedColumn}
                      />
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="recommendation" className="mt-4">
                  <WatermarkRecommendationPanel
                    tableName={tableName}
                    recommendedColumn={recommendedColumn ?? null}
                    allPotentialColumns={columnsData?.allPotentialColumns ?? null}
                    tableInfo={tableInfo}
                  />
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <div className="flex-1">
                {selectedColumn && (
                  <div className="text-sm">
                    <Label>Selected Column:</Label>
                    <code className="ml-2 bg-muted px-2 py-1 rounded text-sm">
                      {selectedColumn}
                    </code>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending || updateSyncTypeMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={handleMarkAsFullSync}
                  disabled={updateMutation.isPending || updateSyncTypeMutation.isPending}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  {updateSyncTypeMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Mark as Full Sync
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!selectedColumn || updateMutation.isPending || updateSyncTypeMutation.isPending}
                >
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Watermark Column
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <>
            {/* For CDC and FULL tables, show info and primary keys */}
            <div className="py-4">
              {tableInfo?.syncTier === 'CDC' ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>This table uses <strong>Change Data Capture (CDC)</strong> via protocol tables.</span>
                  </div>

                  {tableInfo?.primaryKeyColumns && (
                    <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                      <Label className="text-sm font-medium">Primary Key Columns</Label>
                      <div className="flex flex-wrap gap-2">
                        {tableInfo.primaryKeyColumns.split(/[,;]/).map((col: string, idx: number) => (
                          <code key={idx} className="bg-green-50 text-green-700 px-2 py-1 rounded text-sm border border-green-200">
                            {col.trim()}
                          </code>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        These columns are used for upsert operations when syncing CDC changes.
                      </p>
                    </div>
                  )}
                </div>
              ) : tableInfo?.syncTier?.startsWith('FULL_') ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-orange-700">
                    <AlertCircle className="h-5 w-5" />
                    <span>This table uses <strong>Full Refresh</strong> sync - entire table is reloaded each sync.</span>
                  </div>

                  {tableInfo?.primaryKeyColumns && (
                    <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                      <Label className="text-sm font-medium">Primary Key Columns</Label>
                      <div className="flex flex-wrap gap-2">
                        {tableInfo.primaryKeyColumns.split(/[,;]/).map((col: string, idx: number) => (
                          <code key={idx} className="bg-orange-50 text-orange-700 px-2 py-1 rounded text-sm border border-orange-200">
                            {col.trim()}
                          </code>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        These columns are used for upsert operations during full refresh.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-500">
                  <AlertCircle className="h-5 w-5" />
                  <span>Unknown sync tier.</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
